import React, { useEffect, useRef, useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import type { RootState } from '../../store'
import { AuthService } from '../../services/authService'
import { loginSuccess, loginFailure, setLoading } from '../../store/slices/authSlice'
import { initPushNotifications } from '../../services/pushService'

interface ProtectedRouteProps {
  children: React.ReactNode
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading } = useSelector((state: RootState) => state.auth)
  const dispatch = useDispatch()
  const location = useLocation()
  const [isChecking, setIsChecking] = useState(true)
  const authService = new AuthService()

  useEffect(() => {
    const checkAuth = async () => {
      if (isAuthenticated) {
        // Already authenticated (from Redux or localStorage) -- render immediately
        // but revalidate the server session in the background. If the server
        // definitively tells us the session is gone (success: false), we trust
        // that signal and flip to loginFailure so the next render bounces to
        // /login. A thrown network error still leaves state alone (transient).
        setIsChecking(false)
        try {
          const response = await authService.getCurrentUser()
          if (response.success && response.user) {
            dispatch(loginSuccess({
              user: {
                id: response.user.id.toString(),
                username: response.user.username,
                email: response.user.email,
                mfa_enabled: response.user.mfa_enabled,
                email_verified: response.user.email_verified,
                created_at: response.user.created_at,
                avatar: response.user.avatar,
                preferredName: response.user.preferredName
              },
              token: 'session'
            }))
          } else {
            // Server actually responded "not authenticated" (401/302/JSON false).
            // This is distinct from a network throw, so it's safe to sign out.
            dispatch(loginFailure())
          }
        } catch { /* transient network error; keep existing state */ }
        return
      }

      // Not authenticated -- block rendering until we verify with the server
      dispatch(setLoading(true))
      try {
        const response = await authService.getCurrentUser()
        if (response.success && response.user) {
          dispatch(loginSuccess({
            user: {
              id: response.user.id.toString(),
              username: response.user.username,
              email: response.user.email,
              mfa_enabled: response.user.mfa_enabled,
              email_verified: response.user.email_verified,
              created_at: response.user.created_at,
              avatar: response.user.avatar,
              preferredName: response.user.preferredName
            },
            token: 'session'
          }))
        } else {
          dispatch(loginFailure())
        }
      } catch {
        dispatch(loginFailure())
      } finally {
        dispatch(setLoading(false))
        setIsChecking(false)
      }
    }

    checkAuth()
    // Re-run on every client-side route change so a session cleared between
    // navigations (e.g., cookie deleted in another tab, server-side revoke)
    // is caught on the *next* nav instead of whenever the user happens to
    // hit an authed API. Background path stays non-blocking, so this adds
    // at most one /api/current-user round-trip per navigation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])

  const pushInitRef = useRef(false)
  useEffect(() => {
    if (isAuthenticated && !pushInitRef.current) {
      pushInitRef.current = true
      initPushNotifications().catch(() => {})
    }
  }, [isAuthenticated])

  if (isChecking || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-brand"></div>
          <p className="mt-4 text-secondary">Loading...</p>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    // Redirect to /login, preserving the intended target on BOTH:
    //   - `?redirect=<path>` on the URL (so the bookmark survives reloads,
    //      cross-tab opens, and tools that only see the URL such as E2E tests)
    //   - React Router `state.from` (legacy path, still read by <Login/>).
    // The full path (pathname + search + hash) is URL-encoded so query
    // strings and hash fragments round-trip cleanly.
    const intendedPath = `${location.pathname}${location.search}${location.hash}`
    const shouldSkipRedirectParam =
      location.pathname === '/' || location.pathname === '/login'
    const loginUrl = shouldSkipRedirectParam
      ? '/login'
      : `/login?redirect=${encodeURIComponent(intendedPath)}`
    return <Navigate to={loginUrl} state={{ from: location }} replace />
  }

  return <>{children}</>
}

